import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useStudioStore } from '@/store/useStudioStore';
import { useDashboardStore } from '@/store/useDashboardStore';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { projectApi } from '@/api/project';
import { siteApi } from '@/api/site';
import { toast } from 'sonner';
import { trackEvent } from '@/api/track';
import { isSpecialProjectType } from '@/lib/projectUtils.ts';
import { resolveEditableDraft, saveConfirmedDraftSnapshot } from '@/lib/draftUtils';
import {
  buildDraftPreviewHtml,
  ensureStaticRevealContentVisible,
  normalizeGeneratedFormFieldNames,
} from '@/lib/domUtils';
import { normalizeLegacyPlatformDomain } from '@/lib/platformDomain';
import { buildPublishedPageUrl, getPublishedPageDomain } from '@/lib/publishedPageUrl';
import { generateSiteName } from '@/lib/siteName';
import type { Site } from '@/types/site';

const DRAFT_REVIEW_RESOLUTION_TOAST_ID = 'draft-review-resolution';
const PUBLISH_REVIEW_TOAST_ID = 'publish-review-required';
const PUBLISH_ERROR_TOAST_ID = 'publish-error';
const waitForDraftSync = (delayMs: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, delayMs);
  });

type PublishMode = 'existing' | 'new';
const NEW_SITE_PLACEHOLDER_HOST = 'site-xxxxxxxx.site.cove-page.com';

const generateRandomPath = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const toSlug = (str: string) => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * StudioPublishModal - publish confirmation dialog.
 * Lets users choose a destination site and confirm the final page slug before publishing.
 */
export const StudioPublishModal = () => {
  const { t } = useTranslation('studio');
  const {
    isPublishModalOpen,
    setPublishModalOpen,
    setPublishSuccessModalOpen,
    setLastPublishedUrl,
    currentName,
    currentProjectId,
    currentId,
    currentSiteId,
    setSiteId,
    setCurrentSiteInfo,
    setCurrentProjectData,
    currentProject,
    currentSiteInfo,
    currentProjectTypeId,
    currentDraftId,
    currentDraftVersion,
    currentDraftStatus,
    previewReview,
    isBuildDone,
    isBuilding,
    autoPublishContext,
    setAutoPublishContext,
    setGeneratedHtml,
    setCurrentDraftMeta,
    startPreviewReview,
    clearPreviewReview,
  } = useStudioStore();
  const { patchProjectUrl } = useDashboardStore();

  /** Read the published slug from project data first, then localStorage. */
  const getPersistedSlug = useCallback((state: ReturnType<typeof useStudioStore.getState>) => {
    const fromSitePaths = (state.currentProject?.sitePaths?.[0]?.path || '').replace(
      /^\/+|\/+$/g,
      '',
    );
    if (fromSitePaths) return fromSitePaths;
    const id = state.currentId;
    return id ? (localStorage.getItem(`pub_slug_${id}`) ?? '') : '';
  }, []);

  const getDefaultSlug = useCallback(
    (state: ReturnType<typeof useStudioStore.getState>) => {
      const existing = getPersistedSlug(state);
      if (existing) return existing;
      return generateRandomPath();
    },
    [getPersistedSlug],
  );

  const [slug, setSlug] = useState(() => {
    return getDefaultSlug(useStudioStore.getState());
  });
  const [availableSites, setAvailableSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [publishMode, setPublishMode] = useState<PublishMode>('new');
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [siteLoadError, setSiteLoadError] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const autoPublishAttemptedRef = useRef(false);

  useEffect(() => {
    autoPublishAttemptedRef.current = false;
  }, [autoPublishContext?.enabled, autoPublishContext?.source, currentId]);

  useEffect(() => {
    return useStudioStore.subscribe((state, prevState) => {
      if (state.isPublishModalOpen && !prevState.isPublishModalOpen) {
        setSlug(getDefaultSlug(state));
      }
    });
  }, [getDefaultSlug]);

  // Use slug state directly so the input remains fully controlled.
  const displaySlug = slug;

  const [slugWarning, setSlugWarning] = useState('');

  const showSlugWarning = (msg: string) => {
    setSlugWarning(msg);
  };

  const showPublishReviewToast = useCallback((message: string) => {
    toast.dismiss(DRAFT_REVIEW_RESOLUTION_TOAST_ID);
    toast.error(message, { id: PUBLISH_REVIEW_TOAST_ID });
  }, []);

  const openPendingDraftReview = useCallback(
    (pendingReview: Awaited<ReturnType<typeof resolveEditableDraft>>['pendingReview']) => {
      if (!pendingReview) return;

      clearPreviewReview();
      setGeneratedHtml(pendingReview.beforeHtml);
      setCurrentDraftMeta(
        pendingReview.currentDraft.id,
        pendingReview.currentDraft.version,
        pendingReview.currentDraft.draftStatus,
      );
      startPreviewReview({
        draftId: pendingReview.pendingDraft.id,
        version: pendingReview.pendingDraft.version,
        beforeHtml: pendingReview.beforeHtml,
        afterHtml: pendingReview.afterHtml,
        title: 'Review restored version',
        note: 'Compare the current draft with the restored version, then accept the restored draft or keep the current one.',
        sourceMessageId: null,
      });
    },
    [clearPreviewReview, setCurrentDraftMeta, setGeneratedHtml, startPreviewReview],
  );

  const getSiteHost = (site?: Site | null) =>
    getPublishedPageDomain({ site, includeSiteKeyFallback: true }) || 'preview.coveauto.io';

  // Prefer the live site info first because sitePaths can lag behind domain verification.
  const sitePath = currentProject?.sitePaths?.[0];
  const effectivePublishedSiteId =
    currentSiteId ?? (sitePath?.siteId ? String(sitePath.siteId) : null);
  const matchedPublishedSiteInfo =
    currentSiteInfo &&
    effectivePublishedSiteId &&
    currentSiteInfo.id === Number(effectivePublishedSiteId)
      ? currentSiteInfo
      : null;
  const displayDomain =
    getPublishedPageDomain({
      site: matchedPublishedSiteInfo,
      sitePath,
      includeSiteKeyFallback: true,
    }) || 'preview.coveauto.io';
  // Treat a project as published when it has a site path or persisted slug.
  const persistedSlug = currentId ? (localStorage.getItem(`pub_slug_${currentId}`) ?? '') : '';
  const hasPublished = !!(sitePath?.path || persistedSlug);
  const parsedProjectTypeId =
    currentProjectTypeId === null || currentProjectTypeId === ''
      ? NaN
      : Number(currentProjectTypeId);
  const publishProjectTypeId = Number.isFinite(parsedProjectTypeId)
    ? parsedProjectTypeId
    : undefined;
  const trackPublishFailed = useCallback(
    (failStage: string, error?: unknown, extra: Record<string, unknown> = {}) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : String(error || extra.error || failStage);

      trackEvent({
        eventCode: 'app_publish_failed',
        eventData: JSON.stringify({
          app_id: currentProjectId,
          user_project_id: currentId,
          publish_mode: publishMode,
          site_id: currentSiteId ?? selectedSiteId ?? undefined,
          error: errorMessage,
          fail_stage: failStage,
          ...extra,
        }),
      });
    },
    [currentId, currentProjectId, currentSiteId, publishMode, selectedSiteId],
  );

  const selectedSite = availableSites.find((site) => site.id === selectedSiteId) ?? null;
  const publishDisplayDomain =
    !hasPublished && publishMode === 'existing' && selectedSite
      ? getSiteHost(selectedSite)
      : !hasPublished
        ? NEW_SITE_PLACEHOLDER_HOST
        : displayDomain;

  useEffect(() => {
    if (
      !isPublishModalOpen ||
      !hasPublished ||
      !effectivePublishedSiteId ||
      matchedPublishedSiteInfo
    ) {
      return;
    }

    let cancelled = false;

    const loadPublishedSiteInfo = async () => {
      try {
        const response = await siteApi.getSiteList({ pageNumber: 1, pageSize: 500 });
        const site =
          response.list?.find((item) => item.id === Number(effectivePublishedSiteId)) ?? null;
        if (!cancelled && site) {
          setCurrentSiteInfo(site);
        }
      } catch {
        // Keep using the project site path fallback when live site info cannot be refreshed.
      }
    };

    void loadPublishedSiteInfo();

    return () => {
      cancelled = true;
    };
  }, [
    effectivePublishedSiteId,
    hasPublished,
    isPublishModalOpen,
    matchedPublishedSiteInfo,
    setCurrentSiteInfo,
  ]);

  useEffect(() => {
    if (!isPublishModalOpen) return;

    setSlugWarning('');

    if (hasPublished) {
      setAvailableSites([]);
      setSelectedSiteId(null);
      setPublishMode('new');
      setSiteLoadError('');
      setIsLoadingSites(false);
      return;
    }

    let cancelled = false;

    const loadUserSites = async () => {
      setIsLoadingSites(true);
      setSiteLoadError('');

      try {
        const response = await siteApi.getSiteList({
          pageNumber: 1,
          pageSize: 500,
          ...(publishProjectTypeId !== undefined ? { projectTypeId: publishProjectTypeId } : {}),
        });
        const sites = (response.list ?? []).filter((site) => site.status !== -1);

        if (cancelled) return;

        setAvailableSites(sites);
        if (sites.length > 0) {
          setPublishMode('existing');
          setSelectedSiteId(sites[0].id);
        } else {
          setPublishMode('new');
          setSelectedSiteId(null);
        }
      } catch {
        if (cancelled) return;
        setAvailableSites([]);
        setSelectedSiteId(null);
        setPublishMode('new');
        setSiteLoadError('Could not load existing sites. A new site will be created.');
      } finally {
        if (!cancelled) {
          setIsLoadingSites(false);
        }
      }
    };

    void loadUserSites();

    return () => {
      cancelled = true;
    };
  }, [hasPublished, isPublishModalOpen, publishProjectTypeId]);

  const ensureDraftPublishable = useCallback(async () => {
    if (!currentProjectId) {
      toast.error(t('publish.toast.id_missing'));
      trackPublishFailed('missing_project_id', 'Project ID is missing');
      return false;
    }

    if (!isSpecialProjectType(currentProjectTypeId)) {
      if (previewReview.active) {
        showPublishReviewToast('Review the latest draft before publishing.');
        trackPublishFailed('pending_draft_review', 'Review the latest draft before publishing.');
        return false;
      }
      let effectiveDraftStatus = currentDraftStatus;
      if (currentId) {
        const userProjectId = Number(currentId);
        const { editableDraft, pendingReview, saveBaseVersion } =
          await resolveEditableDraft(userProjectId);
        if (pendingReview) {
          openPendingDraftReview(pendingReview);
          showPublishReviewToast('Review the latest draft before publishing.');
          trackPublishFailed('pending_draft_review', 'Review the latest draft before publishing.', {
            draft_id: pendingReview.pendingDraft.id,
            draft_version: pendingReview.pendingDraft.version,
          });
          return false;
        }
        if (editableDraft?.draftStatus) {
          effectiveDraftStatus = editableDraft.draftStatus;
          if (
            String(currentDraftStatus || '') !== String(editableDraft.draftStatus) ||
            String(currentDraftId || '') !== String(editableDraft.id) ||
            currentDraftVersion !== editableDraft.version
          ) {
            setCurrentDraftMeta(editableDraft.id, editableDraft.version, editableDraft.draftStatus);
          }
        } else {
          effectiveDraftStatus = effectiveDraftStatus ?? null;
        }
        if (effectiveDraftStatus === 'CONFIRMED' && editableDraft?.content) {
          const publishContent = normalizeGeneratedFormFieldNames(
            ensureStaticRevealContentVisible(editableDraft.content),
          );
          const needsPublishContentRepair = publishContent !== editableDraft.content;
          const shouldResyncConfirmedDraft =
            typeof saveBaseVersion === 'number' && saveBaseVersion > editableDraft.version;
          if (needsPublishContentRepair || shouldResyncConfirmedDraft) {
            const publishReadyDraft = await saveConfirmedDraftSnapshot(
              userProjectId,
              publishContent,
              needsPublishContentRepair
                ? 'Prepared generated page content for publish.'
                : 'Kept current version after review.',
              {
                suppressErrorToast: true,
                retries: 4,
                minBaseVersion: saveBaseVersion ?? editableDraft.version,
              },
            );

            for (let attempt = 0; attempt < 4; attempt += 1) {
              const syncedLatestDraft = await projectApi.getLatestDraft(userProjectId);
              if (
                syncedLatestDraft &&
                String(syncedLatestDraft.id) === String(publishReadyDraft.id) &&
                syncedLatestDraft.draftStatus === 'CONFIRMED'
              ) {
                break;
              }
              await waitForDraftSync(150);
            }

            setCurrentDraftMeta(
              publishReadyDraft.id,
              publishReadyDraft.version,
              publishReadyDraft.draftStatus,
            );
            if (needsPublishContentRepair) {
              setGeneratedHtml(buildDraftPreviewHtml(publishReadyDraft.content || publishContent));
            }
            effectiveDraftStatus = publishReadyDraft.draftStatus;
          }
        }
      }
      if (effectiveDraftStatus !== 'CONFIRMED') {
        toast.error(
          'Only confirmed drafts can be published. Please confirm the latest draft first.',
        );
        trackPublishFailed('draft_not_confirmed', 'Only confirmed drafts can be published.', {
          draft_status: effectiveDraftStatus,
        });
        return false;
      }
    }
    return true;
  }, [
    currentDraftId,
    currentDraftVersion,
    currentDraftStatus,
    currentId,
    currentProjectId,
    currentProjectTypeId,
    openPendingDraftReview,
    previewReview.active,
    setCurrentDraftMeta,
    setGeneratedHtml,
    showPublishReviewToast,
    t,
    trackPublishFailed,
  ]);

  const publishProjectNow = useCallback(
    async ({
      slugOverride,
      forcedPublishMode,
    }: {
      slugOverride?: string;
      forcedPublishMode?: PublishMode;
    } = {}) => {
      if (isPublishing || isLoadingSites) return;

      const resolvedPublishMode = forcedPublishMode ?? publishMode;
      let publishable: boolean;
      try {
        publishable = await ensureDraftPublishable();
      } catch (err) {
        trackPublishFailed('publish_precheck', err, {
          publish_mode: resolvedPublishMode,
        });
        toast.error((err as { message?: string })?.message || t('publish.toast.error'), {
          id: PUBLISH_ERROR_TOAST_ID,
        });
        return;
      }
      if (!publishable) return;

      const finalSlug = toSlug(slugOverride ?? displaySlug) || generateRandomPath();
      console.log('[Publish] Preparing to publish. Slug:', finalSlug);

      setPublishModalOpen(false);
      setIsPublishing(true);

      let failStage = 'publish_prepare';
      let failureSiteId: string | number | null = currentSiteId ?? selectedSiteId ?? null;

      try {
        const preferredExistingSiteId =
          selectedSiteId != null
            ? String(selectedSiteId)
            : autoPublishContext?.targetSiteId
              ? String(autoPublishContext.targetSiteId)
              : null;
        let effectiveSiteId =
          !hasPublished && resolvedPublishMode === 'existing'
            ? preferredExistingSiteId
            : currentSiteId;
        let effectiveSiteInfo: Site | null =
          !hasPublished && resolvedPublishMode === 'existing'
            ? selectedSite ||
              availableSites.find(
                (site) => String(site.id) === String(preferredExistingSiteId || ''),
              ) ||
              currentSiteInfo
            : currentSiteInfo;
        failureSiteId = effectiveSiteId ?? failureSiteId;
        const isAiWebpage = !isSpecialProjectType(currentProjectTypeId);

        const hasValidSitePath = !!currentProject?.sitePaths?.[0]?.siteId;
        const shouldForceCreateAiSite = isAiWebpage && !hasValidSitePath;
        const shouldCreateNewSite =
          (!hasPublished && resolvedPublishMode === 'new') ||
          !effectiveSiteId ||
          (shouldForceCreateAiSite && !preferredExistingSiteId);

        if (shouldCreateNewSite) {
          console.log(
            shouldForceCreateAiSite
              ? '[Publish] No valid AI webpage site path found. Creating a site...'
              : '[Publish] No site selected. Creating a default site...',
          );
          failStage = 'site_create';
          const newSite = await siteApi.createSite({
            name: generateSiteName(),
            createType: 'PROJECT_PUBLISH',
            projectTypeId: publishProjectTypeId,
          });
          effectiveSiteId = String(newSite.id);
          failureSiteId = effectiveSiteId;
          effectiveSiteInfo = newSite;
          console.log('[Publish] Site created. siteId:', effectiveSiteId);
        }

        let publishPayload: {
          siteId?: number;
          path: string;
          userProjectId?: number;
          projectId?: number;
        };

        if (isAiWebpage) {
          publishPayload = {
            siteId: Number(effectiveSiteId) || undefined,
            path: finalSlug,
            userProjectId: Number(currentId),
          };
        } else {
          publishPayload = {
            siteId: Number(effectiveSiteId) || undefined,
            path: finalSlug,
            projectId: Number(currentProjectId),
          };
        }

        console.log('[Publish] Calling projectApi.publishProject. payload:', publishPayload);
        failStage = 'publish_api';
        failureSiteId = effectiveSiteId ?? failureSiteId;
        const res = await projectApi.publishProject(publishPayload, {
          suppressErrorToast: true,
        });

        console.log('[Publish] Publish API succeeded:', res);
        failStage = 'post_publish_state';
        if (effectiveSiteId) {
          setSiteId(String(effectiveSiteId));
        }
        if (effectiveSiteInfo) {
          setCurrentSiteInfo(effectiveSiteInfo);
        }

        if (currentId) {
          localStorage.setItem(`pub_slug_${currentId}`, finalSlug);
        }

        const earlyRawUrl = res.accessUrl || res.previewUrl;
        const publishedPathEarly = res.publishSubPath || finalSlug;
        const earlyDisplayUrl =
          buildPublishedPageUrl({
            site: effectiveSiteInfo,
            sitePath: currentProject?.sitePaths?.[0],
            path: publishedPathEarly,
            fallbackUrl: earlyRawUrl,
          }) || '';
        if (currentId && earlyDisplayUrl) {
          localStorage.setItem(`pub_url_${currentId}`, earlyDisplayUrl);
        }

        const extractDomain = (url?: string) =>
          (url || '').replace(/^https?:\/\/([^/]+).*/, '$1') || '';
        const publishedOriginalDomain = normalizeLegacyPlatformDomain(
          extractDomain(res.previewUrl) || extractDomain(res.accessUrl),
        );
        const fallbackSiteName = effectiveSiteInfo?.name || currentName || '';
        const fallbackSiteDomain = normalizeLegacyPlatformDomain(effectiveSiteInfo?.domain) || '';
        const fallbackOriginalDomain =
          normalizeLegacyPlatformDomain(effectiveSiteInfo?.originalDomain) ||
          publishedOriginalDomain;

        let updatedProject = currentProject;
        if (!isAiWebpage) {
          try {
            const freshProject = await projectApi.getProjectDetail({
              projectId: Number(currentProjectId),
              tenantId: currentProject?.tenantId ?? 0,
            });
            const nextSitePath = {
              id: freshProject.sitePaths?.[0]?.id || 0,
              siteId: Number(effectiveSiteId),
              siteName: fallbackSiteName,
              domain: fallbackSiteDomain,
              path: res.publishSubPath || finalSlug || '',
              originalDomain: fallbackOriginalDomain,
              status: 1,
              bindTime: freshProject.sitePaths?.[0]?.bindTime || new Date().toISOString(),
            };
            if (!freshProject.sitePaths?.length && effectiveSiteId) {
              freshProject.sitePaths = [nextSitePath];
            } else if (freshProject.sitePaths?.length) {
              freshProject.sitePaths = [nextSitePath, ...freshProject.sitePaths.slice(1)];
            }
            updatedProject = freshProject;
            setCurrentProjectData(updatedProject);
          } catch {
            if (currentProject) {
              updatedProject = {
                ...currentProject,
                sitePaths: [
                  {
                    id: 0,
                    siteId: Number(effectiveSiteId),
                    siteName: fallbackSiteName,
                    domain: fallbackSiteDomain,
                    path: res.publishSubPath || finalSlug || '',
                    originalDomain: fallbackOriginalDomain,
                    status: 1,
                    bindTime: new Date().toISOString(),
                  },
                ],
              };
              setCurrentProjectData(updatedProject);
            }
          }
        } else if (currentProject) {
          updatedProject = {
            ...currentProject,
            sitePaths: [
              {
                id: 0,
                siteId: Number(effectiveSiteId),
                siteName: fallbackSiteName,
                domain: fallbackSiteDomain,
                path: res.publishSubPath || finalSlug || '',
                originalDomain: fallbackOriginalDomain,
                status: 1,
                bindTime: new Date().toISOString(),
              },
            ],
          };
          setCurrentProjectData(updatedProject);
        }

        const publishedPath = res.publishSubPath || finalSlug;
        const rawUrl = res.accessUrl || res.previewUrl;
        const displayUrl =
          buildPublishedPageUrl({
            site: effectiveSiteInfo,
            sitePath: updatedProject?.sitePaths?.[0],
            path: publishedPath,
            fallbackUrl: rawUrl,
          }) || '';
        if (displayUrl) {
          setLastPublishedUrl(displayUrl);
          const userProjectIdStr = currentId ?? currentProjectId;
          if (userProjectIdStr) {
            patchProjectUrl(userProjectIdStr, displayUrl);
          }
        }

        console.log('[Publish] Opening success modal.');
        trackEvent({
          eventCode: 'app_publish_success',
          eventData: JSON.stringify({
            app_id: currentProjectId,
            create_method: 'brief',
            template_id: undefined,
          }),
        });
        setPublishSuccessModalOpen(true);
      } catch (err) {
        console.error('[Publish] Publish failed:', err);
        trackPublishFailed(failStage, err, {
          publish_mode: resolvedPublishMode,
          site_id: failureSiteId ?? undefined,
          slug: finalSlug,
        });
        toast.error((err as { message?: string })?.message || t('publish.toast.error'), {
          id: PUBLISH_ERROR_TOAST_ID,
        });
      } finally {
        setIsPublishing(false);
      }
    },
    [
      currentId,
      currentName,
      currentProject,
      currentProjectId,
      currentProjectTypeId,
      currentSiteId,
      currentSiteInfo,
      displaySlug,
      ensureDraftPublishable,
      hasPublished,
      isLoadingSites,
      isPublishing,
      patchProjectUrl,
      publishMode,
      publishProjectTypeId,
      autoPublishContext?.targetSiteId,
      availableSites,
      selectedSite,
      selectedSiteId,
      setCurrentProjectData,
      setCurrentSiteInfo,
      setLastPublishedUrl,
      setPublishModalOpen,
      setPublishSuccessModalOpen,
      setSiteId,
      t,
      trackPublishFailed,
    ],
  );

  const handlePublish = async () => {
    await publishProjectNow();
  };

  useEffect(() => {
    if (!autoPublishContext?.enabled || autoPublishContext.source !== 'autopilot-clone') return;
    if (autoPublishAttemptedRef.current) return;
    if (
      !isBuildDone ||
      isBuilding !== false ||
      previewReview.active ||
      hasPublished ||
      isPublishing ||
      isLoadingSites
    )
      return;
    if (!currentProjectId || !currentId) return;

    autoPublishAttemptedRef.current = true;

    void publishProjectNow({
      forcedPublishMode:
        autoPublishContext?.targetSiteId || selectedSiteId || currentSiteId ? 'existing' : 'new',
    }).finally(() => {
      setAutoPublishContext(null);
    });
  }, [
    autoPublishContext,
    availableSites,
    currentId,
    currentName,
    currentProjectId,
    currentSiteId,
    hasPublished,
    isBuildDone,
    isBuilding,
    isLoadingSites,
    isPublishing,
    previewReview.active,
    publishProjectNow,
    selectedSiteId,
    setAutoPublishContext,
  ]);

  return (
    <Dialog open={isPublishModalOpen} onOpenChange={setPublishModalOpen}>
      <DialogContent
        className="pub-confirm-modal w-[94%] rounded-[18px] border-none p-0 shadow-[0_20px_56px_rgba(0,0,0,0.13),0_1px_0_rgba(0,0,0,0.04)] focus:outline-none focus-visible:outline-none sm:max-w-[480px]"
        showCloseButton={false}
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-[28px_28px_26px]">
          {/* Title */}
          <DialogTitle className="pub-confirm-title mb-[6px] text-left text-[17px] leading-tight font-[800] tracking-[-0.3px] text-[#0D0D1A]">
            {t('publish.modal_title')}
          </DialogTitle>

          {/* Subtitle */}
          <p className="pub-confirm-sub mb-[20px] text-left text-[13px] leading-[1.55] font-normal text-[#0D0D1A]/56">
            {t(
              'publish.modal_subtitle_site_select',
              'Choose the site for this page, then edit the final path visitors will use.',
            )}
          </p>

          {!hasPublished && (
            <div className="pub-destination mb-[16px]">
              <div className="pub-field-label mb-[8px] text-[11.5px] font-[650] tracking-[0.04em] text-[#0D0D1A]/56 uppercase">
                {t('publish.destination_label', 'Publish destination')}
              </div>

              {isLoadingSites ? (
                <div className="border-border rounded-[10px] border bg-white p-[12px] text-[12px] text-[#0D0D1A]/56">
                  {t('publish.loading_sites', 'Loading sites...')}
                </div>
              ) : availableSites.length > 0 ? (
                <>
                  <div
                    className="pub-destination-tabs border-border mb-[10px] grid grid-cols-2 gap-[4px] rounded-[10px] border bg-[#F7F7FC] p-[4px]"
                    role="group"
                    aria-label={t('publish.destination_label', 'Publish destination')}
                  >
                    <button
                      type="button"
                      aria-pressed={publishMode === 'existing'}
                      onClick={() => {
                        setPublishMode('existing');
                        setSelectedSiteId((current) => current ?? availableSites[0]?.id ?? null);
                      }}
                      className={`h-[34px] cursor-pointer rounded-[7px] border-none bg-transparent text-[12.5px] font-[600] transition-all ${
                        publishMode === 'existing'
                          ? 'bg-white text-[#0D0D1A] shadow-[0_1px_4px_rgba(0,0,0,0.08)]'
                          : 'text-[#0D0D1A]/38'
                      }`}
                    >
                      {t('publish.existing_site', 'Existing site')}
                    </button>
                    <button
                      type="button"
                      aria-pressed={publishMode === 'new'}
                      onClick={() => setPublishMode('new')}
                      className={`h-[34px] cursor-pointer rounded-[7px] border-none bg-transparent text-[12.5px] font-[600] transition-all ${
                        publishMode === 'new'
                          ? 'bg-white text-[#0D0D1A] shadow-[0_1px_4px_rgba(0,0,0,0.08)]'
                          : 'text-[#0D0D1A]/38'
                      }`}
                    >
                      {t('publish.new_site', 'New site')}
                    </button>
                  </div>
                  <div className="pub-destination-body border-border rounded-[10px] border bg-white p-[11px_12px]">
                    {publishMode === 'existing' ? (
                      <div className="pub-existing-list grid max-h-[180px] gap-[6px] overflow-y-auto">
                        {availableSites.map((site) => {
                          const active = selectedSiteId === site.id;

                          return (
                            <button
                              key={site.id}
                              type="button"
                              aria-pressed={active}
                              onClick={() => setSelectedSiteId(site.id)}
                              className={`w-full cursor-pointer rounded-[9px] border bg-white p-[9px_10px] text-left transition-all ${
                                active
                                  ? 'border-[#2563EB]/45 bg-[#2563EB]/5 shadow-[0_0_0_3px_rgba(37,99,235,0.08)]'
                                  : 'border-border hover:border-[#2563EB]/25 hover:bg-[#2563EB]/3'
                              }`}
                            >
                              <span className="block text-[12.5px] font-[600] text-[#0D0D1A]">
                                {site.name || t('publish.untitled_site', 'Untitled site')}
                              </span>
                              <span className="block overflow-hidden font-mono text-[11.5px] text-ellipsis whitespace-nowrap text-[#0D0D1A]/38">
                                {getSiteHost(site)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="grid gap-[8px]">
                        <div className="text-[12px] leading-[1.45] text-[#0D0D1A]/56">
                          {t(
                            'publish.new_site_line',
                            'CoveAuto AI will create a site for this page.',
                          )}
                        </div>
                        <div className="flex min-w-0 items-center gap-[8px] font-mono text-[12px] text-[#0D0D1A]/38">
                          <Icon icon="fa6-solid:link" className="shrink-0 text-[11px]" />
                          <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                            {NEW_SITE_PLACEHOLDER_HOST}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="pub-destination-body border-border grid gap-[8px] rounded-[10px] border bg-white p-[11px_12px]">
                  <div className="text-[12px] leading-[1.45] text-[#0D0D1A]/56">
                    {t('publish.new_site_line', 'CoveAuto AI will create a site for this page.')}
                  </div>
                  <div className="flex min-w-0 items-center gap-[8px] font-mono text-[12px] text-[#0D0D1A]/38">
                    <Icon icon="fa6-solid:link" className="shrink-0 text-[11px]" />
                    <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                      {NEW_SITE_PLACEHOLDER_HOST}
                    </span>
                  </div>
                  {siteLoadError && (
                    <div className="text-[11.5px] leading-[1.5] text-[#D97706]">
                      {siteLoadError}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="pub-field-label mb-[8px] text-[11.5px] font-[650] tracking-[0.04em] text-[#0D0D1A]/56 uppercase">
            {t('publish.page_path_label', 'Page path')}
          </div>

          {/* URL preview */}
          <div className="pub-url-preview border-border mb-0 flex h-[44px] items-center gap-0 overflow-hidden rounded-[10px] border bg-[#F7F7FC] p-[12px_14px] shadow-none">
            <span className="pub-url-domain shrink-0 text-[12px] whitespace-nowrap text-[#0D0D1A]/38">
              {publishDisplayDomain}
            </span>
            <span className="pub-url-sep mx-[1px] shrink-0 text-[12px] text-[#0D0D1A]/38">/</span>
            <div className="pub-url-slug-wrap flex min-w-0 flex-1 items-center">
              <input
                value={displaySlug}
                onChange={(e) => {
                  if (hasPublished) return;
                  const raw = e.target.value;
                  const cleaned = raw.replace(/[^a-zA-Z0-9]/g, '');
                  const val = cleaned.slice(0, 32);
                  setSlug(val);
                  const messages: string[] = [];
                  if (cleaned.length !== raw.length) messages.push('Use letters and numbers only.');
                  if (cleaned.length > 32) messages.push('Use up to 32 characters.');
                  if (messages.length) showSlugWarning(messages.join(' '));
                }}
                onKeyDown={(e) => {
                  if (hasPublished) return;
                  if (e.key.length === 1 && !/^[a-zA-Z0-9]$/.test(e.key)) {
                    e.preventDefault();
                    showSlugWarning('Use letters and numbers only.');
                  } else if (e.key.length === 1 && slug.length >= 32) {
                    e.preventDefault();
                    showSlugWarning('Use up to 32 characters.');
                  }
                }}
                onPaste={(e) => {
                  if (hasPublished) return;
                  e.preventDefault();
                  const raw = e.clipboardData.getData('text');
                  const cleaned = raw.replace(/[^a-zA-Z0-9]/g, '');
                  const combined = (slug + cleaned).slice(0, 32);
                  setSlug(combined);
                  const messages: string[] = [];
                  if (cleaned.length !== raw.length) messages.push('Use letters and numbers only.');
                  if ((slug + cleaned).length > 32) messages.push('Use up to 32 characters.');
                  if (messages.length) showSlugWarning(messages.join(' '));
                }}
                maxLength={32}
                readOnly={hasPublished}
                className={`pub-url-slug w-full min-w-[60px] border-none bg-transparent p-0 text-[13px] font-[600] text-[#0D0D1A] outline-none focus:text-[#6D28D9] focus:ring-0 ${hasPublished ? 'cursor-default select-none' : 'cursor-text'}`}
                spellCheck={false}
                autoComplete="off"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="pub-url-notes mt-[8px] mb-[24px]">
            <div className="pub-url-lock-note text-left text-[11.5px] leading-[1.5] text-[#0D0D1A]/56">
              {t('publish.lock_note')}
            </div>
            {slugWarning && (
              <div className="pub-url-limit-note mt-[6px] text-left text-[11.5px] leading-[1.5] text-[#D97706]">
                {slugWarning}
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="pub-confirm-footer flex justify-end gap-[10px]">
            <button
              type="button"
              onClick={() => setPublishModalOpen(false)}
              className="pub-cancel-btn border-border h-[36px] min-w-[80px] cursor-pointer rounded-[9px] border bg-white px-[18px] text-[13px] font-[600] text-[#0D0D1A]/70 transition-all hover:bg-[#F7F7FA]"
            >
              {t('publish.cancel')}
            </button>
            <button
              type="button"
              onClick={handlePublish}
              disabled={isPublishing || isLoadingSites}
              className="pub-go-btn flex h-[36px] min-w-[100px] cursor-pointer items-center justify-center gap-[7px] rounded-[9px] border-none bg-[linear-gradient(135deg,#6D28D9,#0891B2)] px-[20px] text-[13px] font-[700] text-white transition-opacity hover:opacity-[0.88] disabled:cursor-default disabled:opacity-60"
            >
              <Icon
                icon={isPublishing ? 'fa6-solid:spinner' : 'fa6-solid:rocket'}
                className={`text-[12px] ${isPublishing ? 'animate-spin' : ''}`}
              />
              {isPublishing
                ? t('publish.publishing', 'Publishing...')
                : t('publish.publish', 'Publish')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
